import React, { useState } from 'react';
import { styled } from '@this/constants/themes';
import { observer } from 'mobx-react';
import type { ImageFile } from 'react-dropzone';
import Dropzone from 'react-dropzone';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const ACCEPT_TYPES = 'image/jpeg,image/png,image/webp,application/pdf';

interface Props {
  selectedPath?: string;
  selectedMimeType?: string;
  selectedFileName?: string;
  onSelect: (f: ImageFile) => void;
  onRemove: () => void;
  removable?: boolean;
}

const ReceiptDropzone = observer(
  ({ selectedPath, selectedMimeType, selectedFileName, onSelect, onRemove, removable = true }: Props) => {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [dropError, setDropError] = useState<string | null>(null);
    const isPdf = selectedMimeType === 'application/pdf' || selectedPath?.toLowerCase().endsWith('.pdf');

    const handleDrop = (accepted: ImageFile[], rejected: ImageFile[]) => {
      setDropError(null);
      if (accepted.length > 0) {
        onSelect(accepted[0]);
        return;
      }
      if (rejected.length > 0) {
        if (rejected[0].size > MAX_FILE_SIZE) {
          setDropError('ファイルサイズは20MB以下にしてください');
        } else {
          setDropError('サポートされていないファイル形式です（JPEG・PNG・WebP・PDF）');
        }
      }
    };

    const handleRemove = () => {
      setDropError(null);
      onRemove();
    };

    return (
      <Wrapper>
        {selectedPath ? (
          <PreviewWrapper>
            {isPdf ? (
              <PdfPlaceholder href={selectedPath} target="_blank" rel="noopener noreferrer">
                <PictureAsPdfIcon style={{ fontSize: 48, color: '#d32f2f' }} />
                <PdfLabel>{selectedFileName ?? 'PDF'}</PdfLabel>
              </PdfPlaceholder>
            ) : (
              <Thumbnail src={selectedPath} onClick={() => setLightboxOpen(true)} title="クリックで拡大" />
            )}
            {removable && (
              <div>
                <RemoveButton onClick={handleRemove}>×</RemoveButton>
              </div>
            )}
            {!isPdf && lightboxOpen && (
              <Lightbox onClick={() => setLightboxOpen(false)}>
                <LightboxImage src={selectedPath} onClick={e => e.stopPropagation()} />
              </Lightbox>
            )}
          </PreviewWrapper>
        ) : (
          <>
            <StyledDropzone onDrop={handleDrop} accept={ACCEPT_TYPES} maxSize={MAX_FILE_SIZE}>
              <p>
                ここへ領収書ファイルをドラッグ&amp;ドロップ
                <br />
                もしくは
                <br />
                クリックして領収書ファイルを指定
                <br />
                <HintText>JPEG・PNG・WebP・PDF（最大20MB）</HintText>
              </p>
            </StyledDropzone>
            {dropError && <ErrorText>{dropError}</ErrorText>}
          </>
        )}
      </Wrapper>
    );
  }
);

const Wrapper = styled.div`
  margin-bottom: 40px;
`;

const StyledDropzone = styled(Dropzone)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 110px;
  background: ${props => props.theme.accentColorLight};
  border: 1px dashed #ccc;
  margin: 10px 0 20px;
  cursor: pointer;
  padding: 20px;
`;

const PreviewWrapper = styled.div`
  display: flex;
  align-items: flex-start;
`;

const Thumbnail = styled.img`
  max-width: 120px;
  max-height: 80px;
  object-fit: contain;
  border: 1px solid ${props => props.theme.grayBorderColor};
  cursor: zoom-in;
`;

const RemoveButton = styled.a`
  font-size: 20px;
  font-weight: bold;
  color: ${props => props.theme.grayTextColor};
  margin-left: 10px;
`;

const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  cursor: zoom-out;
`;

const LightboxImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.5);
  cursor: default;
`;

const PdfPlaceholder = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border: 1px solid ${props => props.theme.grayBorderColor};
  border-radius: 4px;
  background: #fff5f5;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    background: #ffebee;
  }
`;

const PdfLabel = styled.span`
  font-size: 11px;
  font-weight: bold;
  color: #d32f2f;
  margin-top: 2px;
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HintText = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 13px;
  color: #555;
`;

const ErrorText = styled.p`
  font-size: 12px;
  color: ${props => props.theme.redColor};
  margin: 0;
`;

export default ReceiptDropzone;
